/**
 * GEAR-2 — My Gear Internal Preview (admin/staff only) · Jun 2026
 *
 * STRICT INVARIANTS (do not relax without explicit nominal GO):
 *   • Admin + staff only (RBAC enforced by AdminLayout sidebar + defensive
 *     guard inside the page).
 *   • Zero DB writes (no API calls, all data inline mock).
 *   • Zero customer-facing surface — admin preview only.
 *   • Zero ConsentGate / consent capture / real notifications / marketplace
 *     activation / paid access wiring.
 *
 * Purpose: materialise the GEAR-WISHLIST-COMMERCE-1 blueprint + GEAR-1
 * schema dry-run as an inspectable admin UI before any apply. Counsel +
 * product can review the model rendered against mock data without DB
 * mutation.
 */
import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";

// QA-I18N-ADMIN-ES-FULL-1b — bilingual section titles + KV labels for this
// admin-only mock preview. Internal contract terms (User Gear Profile,
// User Rackets, String Setups slugs, etc.) preserved as-is because they
// document the future backend collection names; visible UI titles localized.
const ESLABELS = {
  // Section titles
  user_gear_profile_title: "Perfil de Equipamiento de Usuario (mock)",
  user_rackets_title: "Raquetas del usuario (mock)",
  string_setups_title: "Configuraciones de cordaje (mock · una current por raqueta)",
  string_setups_sub: "El índice parcial único `is_current=true · status=active` lo garantiza.",
  rl_autofill_title: "Vista previa de jerarquía de autofill Racket Lab",
  material_wishlist_title: "Wishlist de material (mock)",
  store_access_title: "Acceso de tienda (mock · por tienda)",
  monetization_title: "Configuración admin de monetización (cascada mock)",
  monetization_sub: "Overrides de cascada por país / deporte / vertical",
  offer_inbox_title: "Bandeja de oportunidades de oferta (sólo mock)",
  offer_inbox_sub: "Salida del algoritmo de matching · is_mock=true · cero envío real",
  banner_title: "GEAR-2 vista previa interna · sólo admin/staff · 100% datos mock",
  banner_body_pre: "Esta pantalla materializa el blueprint ",
  banner_body_mid: " + el plan ",
  banner_body_post: " con datos inline para revisión por counsel y producto. ",
  banner_body_strong: "Cero escrituras",
  banner_body_tail: ", cero DB, cero ConsentGate, cero notificaciones reales, cero activación de marketplace, cero customer-facing.",
  // KV labels
  display_name: "Nombre visible",
  visibility_default: "Visibilidad por defecto",
  linked_shop_access: "Acceso de tienda vinculado (mock)",
  trial_days_left: "Días de prueba restantes",
  access_tier_cap: "Límite de tier de acceso",
  legal_zone_required: "Zona legal requerida",
  // Pill labels
  default_lbl: "predeterminado",
  canonical_lbl: "canónica",
  pending_canonical_lbl: "canónica pendiente",
  // Sport_default + Status labels under title
  sport_default_lbl: "Deporte por defecto",
  status_lbl: "Estado",
  brand_lbl: "Marca",
  model_lbl: "Modelo",
  grip_lbl: "Grip",
  overgrip_lbl: "Overgrip",
  dampener_lbl: "Antivibrador",
};
const ENLABELS = {
  user_gear_profile_title: "User Gear Profile (mock)",
  user_rackets_title: "User Rackets (mock)",
  string_setups_title: "String Setups (mock · one current per racket)",
  string_setups_sub: "Partial unique index `is_current=true · status=active` enforces it.",
  rl_autofill_title: "Racket Lab autofill hierarchy preview",
  material_wishlist_title: "Material Wishlist (mock)",
  store_access_title: "Store Access (mock · per-shop)",
  monetization_title: "Admin monetization settings (mock cascade)",
  monetization_sub: "Cascade overrides per country / sport / vertical",
  offer_inbox_title: "Offer opportunities inbox (mock-only)",
  offer_inbox_sub: "Matching algorithm output · is_mock=true · zero real send",
  banner_title: "GEAR-2 internal preview · admin/staff only · 100% mock data",
  banner_body_pre: "This screen materializes the blueprint ",
  banner_body_mid: " + the plan ",
  banner_body_post: " with inline data for counsel & product review. ",
  banner_body_strong: "Zero writes",
  banner_body_tail: ", zero DB, zero ConsentGate, zero real notifications, zero marketplace activation, zero customer-facing.",
  display_name: "Display name",
  visibility_default: "Visibility default",
  linked_shop_access: "Linked shop access (mock)",
  trial_days_left: "Trial days left",
  access_tier_cap: "Access tier cap",
  legal_zone_required: "Legal zone required",
  default_lbl: "default",
  canonical_lbl: "canonical",
  pending_canonical_lbl: "pending canonical",
  sport_default_lbl: "Sport default",
  status_lbl: "Status",
  brand_lbl: "Brand",
  model_lbl: "Model",
  grip_lbl: "Grip",
  overgrip_lbl: "Overgrip",
  dampener_lbl: "Dampener",
};
const useGearLabels = () => {
  const { lang } = useLang();
  return lang === "es" ? ESLABELS : ENLABELS;
};
import {
  ShieldAlert,
  Lock,
  Wrench,
  Heart,
  Store,
  Cog,
  Inbox,
  CircleCheck,
  CircleSlash,
} from "lucide-react";

// ---------------------------------------------------------------------------
// MOCK DATA — local only · NEVER fetched · NEVER written to DB.
// Mirrors GEAR_WISHLIST_COMMERCE_1_STRATEGY_v1.md §1-§5 + GEAR_1_plan.json.
// ---------------------------------------------------------------------------

const MOCK_USER = {
  display_name: "Anna Mock (preview · not a real user)",
  user_id: "USR-MOCK-0001",
  sport_default: "tennis",
  status: "active",
  visibility_settings: { default: "closed" },
  consent_settings: {
    gear_share_consent: false,
    allow_racket_lab_autofill: false,
    allow_shop_outreach: false,
  },
  linked_shop_access_mock: ["SHOP-MOCK-DEMO-MADRID"],
};

const MOCK_RACKETS = [
  {
    id: "RKT-MOCK-001",
    slot: "default",
    label: "Match",
    is_default: true,
    catalog_brand_id: "BRD-WILSON",
    catalog_product_id: "PROD-MOCK-WILSON-BLADE-98",
    custom_brand_name: null,
    custom_model_name: null,
    display_brand: "Wilson",
    display_model: "Blade 98 v9",
    grip_size: "L3",
    overgrip_ref: "Tourna Mega Tac",
    dampener_ref: "Wilson Pro Feel",
    technical_notes: "Cordaje fresco antes de cada torneo.",
    feel_preferences: {
      control: 5,
      power: 3,
      spin: 4,
      comfort: 4,
      durability: 3,
      arm_sensitivity: "mild",
    },
    status: "active",
  },
  {
    id: "RKT-MOCK-002",
    slot: "racket_2",
    label: "Entrenamiento",
    is_default: false,
    catalog_brand_id: "BRD-WILSON",
    catalog_product_id: "PROD-MOCK-WILSON-CLASH-100",
    display_brand: "Wilson",
    display_model: "Clash 100 v3",
    grip_size: "L3",
    overgrip_ref: "Wilson Pro Overgrip",
    technical_notes: "Más blanda; sesiones largas de mucha repetición.",
    feel_preferences: {
      control: 3,
      power: 4,
      spin: 3,
      comfort: 5,
      durability: 3,
      arm_sensitivity: "mild",
    },
    status: "active",
  },
  {
    id: "RKT-MOCK-003",
    slot: "racket_3",
    label: "Reserva lluvia",
    is_default: false,
    catalog_brand_id: null,
    catalog_product_id: null,
    custom_brand_name: "Prince (pending canonical)",
    custom_model_name: "Phantom 100X 305 (pending)",
    display_brand: "Prince",
    display_model: "Phantom 100X 305",
    grip_size: "L3",
    technical_notes: "Marca aún no resuelta en Catalog Intelligence (Prince → W007 pendiente).",
    feel_preferences: {
      control: 4,
      power: 3,
      spin: 3,
      comfort: 4,
      durability: 4,
      arm_sensitivity: "none",
    },
    status: "active",
  },
];

const MOCK_STRING_SETUPS = [
  {
    id: "STR-MOCK-001",
    user_racket_id: "RKT-MOCK-001",
    catalog_string_vertical_id: "PROD-MOCK-LUXILON-ALU-POWER",
    catalog_string_horizontal_id: "PROD-MOCK-WILSON-NXT",
    display_vertical: "Luxilon Alu Power 125",
    display_horizontal: "Wilson NXT 130",
    tension_vertical: 24.0,
    tension_horizontal: 23.0,
    tension_unit: "kg",
    hybrid: true,
    pre_stretch: false,
    string_origin: "racket_lab",
    last_strung_at: "2026-05-30",
    is_current: true,
    preferred_feel: { control: 5, comfort: 3 },
    notes: "Hybrid clásico control + comfort. Anna prefiere ±0.5 kg después de cada 2 partidos.",
    status: "active",
  },
  {
    id: "STR-MOCK-002",
    user_racket_id: "RKT-MOCK-002",
    catalog_string_vertical_id: "PROD-MOCK-WILSON-NXT",
    catalog_string_horizontal_id: null,
    display_vertical: "Wilson NXT 130 (full bed)",
    display_horizontal: "—",
    tension_vertical: 22.0,
    tension_horizontal: 22.0,
    tension_unit: "kg",
    hybrid: false,
    pre_stretch: false,
    string_origin: "self",
    last_strung_at: "2026-06-02",
    is_current: true,
    preferred_feel: { comfort: 5 },
    notes: "Full multifilamento; sesión técnica larga.",
    status: "active",
  },
  {
    id: "STR-MOCK-003",
    user_racket_id: "RKT-MOCK-003",
    catalog_string_vertical_id: null,
    catalog_string_horizontal_id: null,
    custom_string_vertical_name: "Tecnifibre Black Code (pending canonical)",
    display_vertical: "Tecnifibre Black Code 125 (pending)",
    display_horizontal: "—",
    tension_vertical: 23.0,
    tension_horizontal: 23.0,
    tension_unit: "kg",
    hybrid: false,
    string_origin: "third_party",
    last_strung_at: "2026-04-15",
    is_current: true,
    notes: "Cordaje fallback free-text — pending catalog upgrade non-destructivo.",
    status: "active",
  },
];

const AUTOFILL_HIERARCHY_DEMO = {
  field: "tension_vertical (kg)",
  layers: [
    {
      key: "manual_override",
      label: "1. Manual override del ticket",
      value: "25.0",
      wins: true,
      note: "Operador escribió valor en el form → top wins.",
    },
    {
      key: "customer_profile",
      label: "2. Customer/Racket profile (my_gear default)",
      value: "24.0",
      wins: false,
      note: "Sería el valor sugerido desde Anna · Racket 1.",
    },
    { key: "shop_preset", label: "3. Shop preset", value: "23.0", wins: false, note: "Default de SHOP-MOCK-DEMO-MADRID." },
    { key: "operator_preset", label: "4. Operator preset", value: "22.5", wins: false, note: "Tensión preferida del operador." },
    { key: "blank", label: "5. Blank", value: "—", wins: false, note: "Si todo está vacío, queda manual." },
  ],
  resolved_value: "25.0 kg",
  resolved_from: "manual_override",
};

const MOCK_WISHLIST = [
  {
    id: "WSH-MOCK-001",
    category: "rackets",
    display_product: "Wilson Blade 98 v9 (own brand upgrade · L3)",
    catalog_product_id: "PROD-MOCK-WILSON-BLADE-98",
    priority: "soon",
    intent: "wants_to_buy",
    target_price_range: { min: 220, max: 280, currency: "EUR", locale: "es-ES" },
    notes: "Anna quiere actualizar a v10 cuando salga.",
    status: "active",
  },
  {
    id: "WSH-MOCK-002",
    category: "strings",
    display_product: "Luxilon Alu Power 125 (12-pack)",
    catalog_product_id: "PROD-MOCK-LUXILON-ALU-POWER",
    priority: "urgent",
    intent: "needs_replacement",
    notes: "Stock bajo en casa.",
    status: "active",
  },
  {
    id: "WSH-MOCK-003",
    category: "overgrips",
    display_product: "Tourna Mega Tac (10 pack)",
    catalog_product_id: null,
    custom_product_name: "Tourna Mega Tac 10-pack (pending)",
    priority: "nice_to_have",
    intent: "compare_later",
    notes: "Tourna W007 brand fix pendiente.",
    status: "active",
  },
  {
    id: "WSH-MOCK-004",
    category: "dampeners",
    display_product: "Wilson Pro Feel (3-pack)",
    catalog_product_id: null,
    priority: "nice_to_have",
    intent: "wants_to_try",
    status: "active",
  },
  {
    id: "WSH-MOCK-005",
    category: "footwear",
    display_product: "ASICS Solution Speed FF3 — clay · 39 EU",
    catalog_product_id: null,
    custom_product_name: "ASICS Solution Speed FF3 (pending)",
    priority: "soon",
    intent: "wants_to_buy",
    notes: "Pista de tierra Madrid.",
    status: "active",
  },
];

const MOCK_STORE_ACCESS = {
  shop_id: "SHOP-MOCK-DEMO-MADRID",
  shop_display_name: "Demo Padel & Tennis Madrid (mock)",
  service_status: "trial",
  trial_days_left: 14,
  country_scope: "ES",
  sport_scope: "tennis",
  vertical_scope: "shop",
  access_tier_cap: "L2",
  legal_zone_required: ["gear_share_consent", "shop_named_share"],
  user_permission: {
    permission_status: "pending",
    visibility_level: "L0",
    allowed_data_categories: [],
    consent_snapshot: "—",
    revoked_at: null,
    expires_at: null,
  },
  offer_eligibility: false,
};

const MOCK_MONETIZATION_SETTINGS = [
  { country: "ES", sport: "tennis", vertical: "shop", service_status: "trial", trial_days: 30, paid_by_shop: false, beta_flag: false, legal_gate: "open", consent_gate: "BLOCKED" },
  { country: "ES", sport: "padel", vertical: "shop", service_status: "open_free", trial_days: 0, paid_by_shop: false, beta_flag: false, legal_gate: "open", consent_gate: "BLOCKED" },
  { country: "IT", sport: "tennis", vertical: "shop", service_status: "paid_by_shop", trial_days: 14, paid_by_shop: true, beta_flag: false, legal_gate: "review", consent_gate: "BLOCKED" },
  { country: "FR", sport: "tennis", vertical: "shop", service_status: "beta_internal_only", trial_days: 0, paid_by_shop: false, beta_flag: true, legal_gate: "review", consent_gate: "BLOCKED" },
  { country: "US", sport: "tennis", vertical: "shop", service_status: "disabled", trial_days: 0, paid_by_shop: false, beta_flag: false, legal_gate: "blocked", consent_gate: "BLOCKED" },
];

const MOCK_OFFER_OPPORTUNITIES = [
  {
    id: "OPP-MOCK-001",
    shop_id: "SHOP-MOCK-DEMO-MADRID",
    user_id: "USR-MOCK-0001",
    wishlist_item_id: "WSH-MOCK-001",
    catalog_product_id: "PROD-MOCK-WILSON-BLADE-98",
    opportunity_status: "held_for_consent",
    is_mock: true,
    send_blocked_reason: "missing_consent",
    matching_algorithm_version: "v0.1-mock",
    matched_at: "2026-06-14T12:00:00Z",
  },
  {
    id: "OPP-MOCK-002",
    shop_id: "SHOP-MOCK-DEMO-MADRID",
    user_id: "USR-MOCK-0001",
    wishlist_item_id: "WSH-MOCK-002",
    catalog_product_id: "PROD-MOCK-LUXILON-ALU-POWER",
    opportunity_status: "ready_for_send_blocked",
    is_mock: true,
    send_blocked_reason: "always_mock_in_mvp",
    matching_algorithm_version: "v0.1-mock",
    matched_at: "2026-06-14T12:05:00Z",
  },
];

// ---------------------------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------------------------

const Banner = () => {
  const L = useGearLabels();
  return (
  <div
    data-testid="gear-preview-banner"
    className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4"
  >
    <div className="flex items-start gap-3">
      <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" />
      <div className="text-xs text-amber-900">
        <p className="font-semibold">{L.banner_title}</p>
        <p className="mt-1 leading-5">
          {L.banner_body_pre}<code className="font-mono">GEAR-WISHLIST-COMMERCE-1</code>
          {L.banner_body_mid}<code className="font-mono">GEAR-1</code>
          {L.banner_body_post}<strong>{L.banner_body_strong}</strong>{L.banner_body_tail}
        </p>
      </div>
    </div>
  </div>
);
};

const SectionCard = ({ icon: Icon, title, subtitle, children, testId }) => (
  <section
    data-testid={testId}
    className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
  >
    <header className="mb-4 flex items-center gap-3 border-b border-slate-100 pb-3">
      {Icon ? <Icon className="h-5 w-5 text-emerald-700" /> : null}
      <div>
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {subtitle ? (
          <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
        ) : null}
      </div>
    </header>
    {children}
  </section>
);

const KeyValue = ({ k, v, mono = false }) => (
  <div className="flex items-baseline gap-2 text-xs">
    <span className="font-medium text-slate-500">{k}:</span>
    <span className={mono ? "font-mono text-slate-800" : "text-slate-800"}>
      {v ?? "—"}
    </span>
  </div>
);

const Pill = ({ tone = "slate", children, testId }) => {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    emerald: "bg-emerald-100 text-emerald-800",
    amber: "bg-amber-100 text-amber-800",
    rose: "bg-rose-100 text-rose-800",
    indigo: "bg-indigo-100 text-indigo-800",
  };
  return (
    <span
      data-testid={testId}
      className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${tones[tone]}`}
    >
      {children}
    </span>
  );
};

const UserProfileCard = () => {
  const L = useGearLabels();
  return (
  <SectionCard
    icon={Wrench}
    title={L.user_gear_profile_title}
    subtitle={`${L.sport_default_lbl}: ${MOCK_USER.sport_default} · ${L.status_lbl}: ${MOCK_USER.status}`}
    testId="gear-preview-user-profile"
  >
    <div className="grid gap-2 sm:grid-cols-2">
      <KeyValue k={L.display_name} v={MOCK_USER.display_name} />
      <KeyValue k="user_id (mock)" v={MOCK_USER.user_id} mono />
      <KeyValue
        k={L.visibility_default}
        v={MOCK_USER.visibility_settings.default}
      />
      <KeyValue
        k={L.linked_shop_access}
        v={MOCK_USER.linked_shop_access_mock.join(", ")}
        mono
      />
    </div>
    <div className="mt-3 flex flex-wrap gap-2">
      {Object.entries(MOCK_USER.consent_settings).map(([k, v]) => (
        <Pill
          key={k}
          tone={v ? "emerald" : "slate"}
          testId={`gear-preview-consent-${k}`}
        >
          {k}: {v ? "true" : "false"}
        </Pill>
      ))}
    </div>
  </SectionCard>
  );
};

const RacketsCard = () => {
  const L = useGearLabels();
  return (
  <SectionCard
    icon={Wrench}
    title={L.user_rackets_title}
    subtitle={`${MOCK_RACKETS.length} rackets · 1 default racket per user per sport (partial unique)`}
    testId="gear-preview-rackets"
  >
    <ul className="space-y-3">
      {MOCK_RACKETS.map((r) => (
        <li
          key={r.id}
          data-testid={`gear-preview-racket-${r.id}`}
          className="rounded-lg border border-slate-200 bg-slate-50/40 p-3"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] text-slate-500">{r.id}</span>
            <span className="text-sm font-semibold text-slate-900">
              {r.label}
            </span>
            <Pill tone={r.is_default ? "emerald" : "slate"}>
              {r.slot}
              {r.is_default ? " · default" : ""}
            </Pill>
            {r.catalog_product_id ? (
              <Pill tone="indigo">canonical · {r.catalog_brand_id}</Pill>
            ) : (
              <Pill tone="amber">pending canonical (free-text fallback)</Pill>
            )}
          </div>
          <div className="mt-2 grid gap-1 sm:grid-cols-2">
            <KeyValue k={L.brand_lbl} v={r.display_brand} />
            <KeyValue k={L.model_lbl} v={r.display_model} />
            <KeyValue k={L.grip_lbl} v={r.grip_size} />
            <KeyValue k={L.overgrip_lbl} v={r.overgrip_ref} />
            {r.dampener_ref ? <KeyValue k={L.dampener_lbl} v={r.dampener_ref} /> : null}
            <KeyValue k={L.status_lbl} v={r.status} />
          </div>
          {r.technical_notes ? (
            <p className="mt-2 text-xs italic text-slate-600">
              {r.technical_notes}
            </p>
          ) : null}
          <div className="mt-2 flex flex-wrap gap-1">
            {Object.entries(r.feel_preferences).map(([k, v]) => (
              <Pill key={k} tone="slate">
                {k}: {String(v)}
              </Pill>
            ))}
          </div>
        </li>
      ))}
    </ul>
  </SectionCard>
  );
};

const StringSetupsCard = () => {
  const L = useGearLabels();
  return (
  <SectionCard
    icon={Wrench}
    title={L.string_setups_title}
    subtitle={L.string_setups_sub}
    testId="gear-preview-string-setups"
  >
    <ul className="space-y-3">
      {MOCK_STRING_SETUPS.map((s) => {
        const racket = MOCK_RACKETS.find((r) => r.id === s.user_racket_id);
        return (
          <li
            key={s.id}
            data-testid={`gear-preview-string-setup-${s.id}`}
            className="rounded-lg border border-slate-200 bg-slate-50/40 p-3"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] text-slate-500">
                {s.id}
              </span>
              <span className="text-xs font-semibold text-slate-800">
                {racket?.label || s.user_racket_id}
              </span>
              <Pill tone={s.hybrid ? "indigo" : "slate"}>
                {s.hybrid ? "hybrid" : "full bed"}
              </Pill>
              <Pill tone="emerald">is_current=true</Pill>
              {!s.catalog_string_vertical_id ? (
                <Pill tone="amber">vertical pending canonical</Pill>
              ) : null}
            </div>
            <div className="mt-2 grid gap-1 sm:grid-cols-2">
              <KeyValue
                k="Main (vertical)"
                v={`${s.display_vertical} · ${s.tension_vertical} ${s.tension_unit}`}
              />
              <KeyValue
                k="Cross (horizontal)"
                v={
                  s.hybrid
                    ? `${s.display_horizontal} · ${s.tension_horizontal} ${s.tension_unit}`
                    : "—"
                }
              />
              <KeyValue k="String origin" v={s.string_origin} />
              <KeyValue k="Last strung" v={s.last_strung_at} mono />
            </div>
            {s.notes ? (
              <p className="mt-2 text-xs italic text-slate-600">{s.notes}</p>
            ) : null}
          </li>
        );
      })}
    </ul>
  </SectionCard>
  );
};

const AutofillHierarchyCard = () => {
  const L = useGearLabels();
  return (
  <SectionCard
    icon={Cog}
    title={L.rl_autofill_title}
    subtitle={`Field: ${AUTOFILL_HIERARCHY_DEMO.field} · Resolved: ${AUTOFILL_HIERARCHY_DEMO.resolved_value} (from ${AUTOFILL_HIERARCHY_DEMO.resolved_from})`}
    testId="gear-preview-autofill-hierarchy"
  >
    <p className="mb-3 rounded border border-indigo-200 bg-indigo-50 p-2 text-xs text-indigo-900">
      <strong>Hierarchy (top wins):</strong> manual override &gt; customer/racket
      profile &gt; shop preset &gt; operator preset &gt; blank.
    </p>
    <ol className="space-y-2">
      {AUTOFILL_HIERARCHY_DEMO.layers.map((l) => (
        <li
          key={l.key}
          data-testid={`gear-preview-autofill-layer-${l.key}`}
          className={`flex items-start gap-3 rounded-lg border p-2 ${
            l.wins
              ? "border-emerald-300 bg-emerald-50"
              : "border-slate-200 bg-white"
          }`}
        >
          {l.wins ? (
            <CircleCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-700" />
          ) : (
            <CircleSlash className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" />
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-900">
                {l.label}
              </span>
              <span className="font-mono text-xs text-slate-600">{l.value}</span>
              {l.wins ? <Pill tone="emerald">WINS</Pill> : null}
            </div>
            <p className="mt-0.5 text-[11px] text-slate-500">{l.note}</p>
          </div>
        </li>
      ))}
    </ol>
  </SectionCard>
  );
};

const WishlistCard = () => {
  const L = useGearLabels();
  return (
  <SectionCard
    icon={Heart}
    title={L.material_wishlist_title}
    subtitle={`${MOCK_WISHLIST.length} items across multiple categories`}
    testId="gear-preview-wishlist"
  >
    <ul className="space-y-2">
      {MOCK_WISHLIST.map((w) => (
        <li
          key={w.id}
          data-testid={`gear-preview-wishlist-item-${w.id}`}
          className="rounded border border-slate-200 bg-white p-2"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] text-slate-500">
              {w.id}
            </span>
            <Pill tone="indigo">{w.category}</Pill>
            <span className="text-xs font-semibold text-slate-900">
              {w.display_product}
            </span>
            <Pill
              tone={
                w.priority === "urgent"
                  ? "rose"
                  : w.priority === "soon"
                  ? "amber"
                  : "slate"
              }
            >
              {w.priority}
            </Pill>
            <Pill tone="slate">intent: {w.intent}</Pill>
            {!w.catalog_product_id ? (
              <Pill tone="amber">{L.pending_canonical_lbl}</Pill>
            ) : null}
          </div>
          {w.target_price_range ? (
            <p className="mt-1 text-[11px] text-slate-500">
              Target {w.target_price_range.min}–{w.target_price_range.max}{" "}
              {w.target_price_range.currency} · {w.target_price_range.locale}
            </p>
          ) : null}
          {w.notes ? (
            <p className="mt-0.5 text-[11px] italic text-slate-500">
              {w.notes}
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  </SectionCard>
  );
};

const StoreAccessCard = () => {
  const a = MOCK_STORE_ACCESS;
  const L = useGearLabels();
  return (
    <SectionCard
      icon={Store}
      title={L.store_access_title}
      subtitle={`${a.shop_display_name} · service: ${a.service_status}`}
      testId="gear-preview-store-access"
    >
      <div className="mb-3 grid gap-1 sm:grid-cols-2">
        <KeyValue k="shop_id" v={a.shop_id} mono />
        <KeyValue
          k="Country · Sport · Vertical"
          v={`${a.country_scope} · ${a.sport_scope} · ${a.vertical_scope}`}
        />
        <KeyValue k="Service status" v={a.service_status} />
        <KeyValue k={L.trial_days_left} v={a.trial_days_left} />
        <KeyValue
          k={L.access_tier_cap}
          v={a.access_tier_cap}
        />
        <KeyValue
          k={L.legal_zone_required}
          v={a.legal_zone_required.join(", ")}
        />
      </div>
      <div
        data-testid="gear-preview-store-access-permission"
        className="rounded border border-slate-200 bg-slate-50 p-2"
      >
        <p className="mb-1 text-xs font-semibold text-slate-700">
          User permission for this shop
        </p>
        <div className="grid gap-1 sm:grid-cols-2">
          <KeyValue
            k="permission_status"
            v={a.user_permission.permission_status}
          />
          <KeyValue
            k="visibility_level"
            v={a.user_permission.visibility_level}
          />
          <KeyValue
            k="allowed_data_categories"
            v={
              a.user_permission.allowed_data_categories.length === 0
                ? "—"
                : a.user_permission.allowed_data_categories.join(", ")
            }
          />
          <KeyValue
            k="offer_eligibility"
            v={a.offer_eligibility ? "yes" : "NO (consent gate blocked)"}
          />
        </div>
      </div>
      <p className="mt-2 flex items-center gap-2 rounded bg-rose-50 px-2 py-1 text-[11px] text-rose-800">
        <Lock className="h-3 w-3" />
        Customer-facing UI is HARD BLOCKED until ConsentGate approval per
        jurisdiction.
      </p>
    </SectionCard>
  );
};

const MonetizationCard = () => {
  const L = useGearLabels();
  return (
  <SectionCard
    icon={Cog}
    title={L.monetization_title}
    subtitle={L.monetization_sub}
    testId="gear-preview-monetization"
  >
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="border-b border-slate-200 text-left text-slate-500">
          <tr>
            <th className="py-1 pr-2">Country</th>
            <th className="py-1 pr-2">Sport</th>
            <th className="py-1 pr-2">Vertical</th>
            <th className="py-1 pr-2">Service status</th>
            <th className="py-1 pr-2">Trial days</th>
            <th className="py-1 pr-2">Paid by shop</th>
            <th className="py-1 pr-2">Beta</th>
            <th className="py-1 pr-2">Legal gate</th>
            <th className="py-1 pr-2">Consent gate</th>
          </tr>
        </thead>
        <tbody>
          {MOCK_MONETIZATION_SETTINGS.map((row, i) => (
            <tr
              key={i}
              data-testid={`gear-preview-monetization-row-${row.country}-${row.sport}`}
              className="border-b border-slate-100"
            >
              <td className="py-1 pr-2 font-mono">{row.country}</td>
              <td className="py-1 pr-2">{row.sport}</td>
              <td className="py-1 pr-2">{row.vertical}</td>
              <td className="py-1 pr-2">
                <Pill
                  tone={
                    row.service_status === "disabled"
                      ? "rose"
                      : row.service_status === "trial"
                      ? "amber"
                      : row.service_status === "paid_by_shop"
                      ? "indigo"
                      : row.service_status === "beta_internal_only"
                      ? "slate"
                      : "emerald"
                  }
                >
                  {row.service_status}
                </Pill>
              </td>
              <td className="py-1 pr-2">{row.trial_days}</td>
              <td className="py-1 pr-2">{row.paid_by_shop ? "yes" : "—"}</td>
              <td className="py-1 pr-2">{row.beta_flag ? "yes" : "—"}</td>
              <td className="py-1 pr-2">
                <Pill
                  tone={
                    row.legal_gate === "open"
                      ? "emerald"
                      : row.legal_gate === "review"
                      ? "amber"
                      : "rose"
                  }
                >
                  {row.legal_gate}
                </Pill>
              </td>
              <td className="py-1 pr-2">
                <Pill tone="rose">{row.consent_gate}</Pill>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <p className="mt-2 text-[11px] italic text-slate-500">
      Default José (canónico): &ldquo;Default business availability may be ON at
      platform/shop level, but user-level data visibility remains
      permission-controlled and legal-gated before public activation.&rdquo;
    </p>
  </SectionCard>
  );
};

const OfferOpportunitiesCard = () => {
  const L = useGearLabels();
  return (
  <SectionCard
    icon={Inbox}
    title={L.offer_inbox_title}
    subtitle={L.offer_inbox_sub}
    testId="gear-preview-offer-opportunities"
  >
    <ul className="space-y-2">
      {MOCK_OFFER_OPPORTUNITIES.map((o) => (
        <li
          key={o.id}
          data-testid={`gear-preview-opportunity-${o.id}`}
          className="rounded border border-slate-200 bg-white p-2"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] text-slate-500">{o.id}</span>
            <Pill tone="indigo">{o.opportunity_status}</Pill>
            <Pill tone="rose">is_mock=true</Pill>
            <Pill tone="amber">blocked: {o.send_blocked_reason}</Pill>
          </div>
          <div className="mt-1 grid gap-1 sm:grid-cols-3">
            <KeyValue k="shop_id" v={o.shop_id} mono />
            <KeyValue k="user_id" v={o.user_id} mono />
            <KeyValue
              k="catalog_product_id"
              v={o.catalog_product_id}
              mono
            />
            <KeyValue k="wishlist_item_id" v={o.wishlist_item_id} mono />
            <KeyValue k="algorithm" v={o.matching_algorithm_version} />
            <KeyValue k="matched_at" v={o.matched_at} mono />
          </div>
        </li>
      ))}
    </ul>
    <p className="mt-3 flex items-center gap-2 rounded bg-rose-50 px-2 py-1 text-[11px] text-rose-800">
      <Lock className="h-3 w-3" />
      Cero envío real · cero email · cero WhatsApp · cero SMS · cero push · cero
      marketplace checkout. Bloqueado hasta `WISHLIST-STORE-3` con consent y
      counsel sign-off.
    </p>
  </SectionCard>
  );
};

const AdminGearPreview = () => {
  // Defense-in-depth: backend gate would also reject non-admin/staff, but
  // since this page has zero API calls we enforce it client-side too.
  const { user } = useAuth();
  const role = user?.role;
  if (role !== "admin" && role !== "staff") {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div
      data-testid="admin-gear-preview"
      className="mx-auto max-w-5xl px-4 py-6"
    >
      <header className="mb-4">
        <h1 className="text-2xl font-semibold text-slate-900">
          My Gear · Internal Preview (GEAR-2)
        </h1>
        <p className="mt-1 text-xs text-slate-500">
          Admin/Staff only · 100% mock data · cero writes · cero customer-facing.
        </p>
      </header>

      <Banner />

      <UserProfileCard />
      <RacketsCard />
      <StringSetupsCard />
      <AutofillHierarchyCard />
      <WishlistCard />
      <StoreAccessCard />
      <MonetizationCard />
      <OfferOpportunitiesCard />

      <footer
        data-testid="gear-preview-footer-legal"
        className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px] italic text-slate-600"
      >
        Legal package accepted / counsel OK for authorized internal/admin-only
        technical work. Public activation remains blocked until explicit
        nominal GO.
      </footer>
    </div>
  );
};

export default AdminGearPreview;
